import { Component, Prop, Vue } from 'vue-property-decorator';
import WithRender from './credentials.html';
import config from '@/settings/config.js';
import _axios from '@/services/axios';
import Loading from 'vue-loading-overlay';
import { DeveloperBalanceDto, DeveloperCredentialsDto } from '@/Dtos/DeveloperCredentialsDto';

@WithRender
@Component({
  components: {
    Loading
  }
})
export default class Credentials extends Vue {
  @Prop({ required: true }) readonly environment!: string;

  isLoading = true;
  loaded = false;
  fundsFailed = false;
  credentials: DeveloperCredentialsDto | null = null;
  balance: DeveloperBalanceDto | null = null;
  balanceFailed = false;

  // Kept in this component only, so the secret never reaches browser storage.
  secret = '';

  get isLive() {
    return this.environment === 'live';
  }

  get credentialsUrl() {
    return `${config.gateway}customerdata/DeveloperCredentials/${this.environment}`;
  }

  async mounted() {
    try {
      const response = await _axios.get<DeveloperCredentialsDto>(this.credentialsUrl);
      this.credentials = response.data;
      this.loaded = true;
      await this.loadBalance();
    } catch (error) {
      const err = error as any;
      if (err.response && err.response.status == 404)
        this.loaded = true;
      else
        this.ErrorNotification(this.ErrorMessage(err), 'danger');
    }
    finally {
      this.isLoading = false;
    }
  }

  // Only Test shows a balance here, because the Balance page already covers the live wallet.
  async loadBalance() {
    if (this.isLive)
      return;

    try {
      const response = await _axios.get<DeveloperBalanceDto>(`${this.credentialsUrl}/balance`);
      this.balance = response.data;
      this.balanceFailed = false;
    } catch (error) {
      this.balance = null;
      this.balanceFailed = true;
    }
  }

  async generate() {
    if (this.isLive && !await this.confirm('Create live credentials?',
      'These credentials spend real money from your Umbai wallet. Keep them on your server and never share them.', 'Create'))
      return;

    await this.saveCredentials(this.credentialsUrl);
  }

  async regenerate() {
    if (await this.confirm('Regenerate secret?',
      'Your current secret will stop working immediately, and any integration using it will fail until you update it.', 'Regenerate'))
      await this.saveCredentials(`${this.credentialsUrl}/regenerate`);
  }

  async copy(value: string) {
    try {
      await navigator.clipboard.writeText(value);
      this.ErrorNotification('Copied', 'success');
    } catch (error) {
      this.ErrorNotification('Could not copy, please select the text and copy it yourself', 'warning');
    }
  }

  downloadEnvironment() {
    const credentials = this.credentials as DeveloperCredentialsDto;
    const name = this.isLive ? 'live' : 'test';
    const values = [
      { key: 'gatewayUrl', value: credentials.gatewayUrl, enabled: true },
      { key: 'identityUrl', value: credentials.identityUrl, enabled: true },
      { key: 'clientId', value: credentials.clientId, enabled: true },
      { key: 'clientSecret', value: this.secret, enabled: true },
      { key: 'token', value: '', enabled: true },
      { key: 'quoteId', value: '', enabled: true },
      { key: 'reference', value: '', enabled: true }
    ];
    const environment = { name: `Umbai ${this.isLive ? 'Live' : 'Test'}`, values, _postman_variable_scope: 'environment' };

    const url = URL.createObjectURL(new Blob([JSON.stringify(environment, null, 2)], { type: 'application/json' }));
    const link = document.createElement('a');
    link.href = url;
    link.download = `umbai-${name}.postman_environment.json`;
    link.click();
    URL.revokeObjectURL(url);
  }

  private confirm(title: string, message: string, okTitle: string): Promise<boolean> {
    return this.$bvModal.msgBoxConfirm(message, { title, okVariant: 'danger', okTitle, centered: true });
  }

  private async saveCredentials(url: string) {
    try {
      this.isLoading = true;
      const response = await _axios.post<DeveloperCredentialsDto>(url);
      this.credentials = response.data;
      this.secret = response.data.clientSecret;
      this.fundsFailed = !this.isLive && !response.data.testFundsCredited;
      await this.loadBalance();
    } catch (error) {
      this.ErrorNotification(this.ErrorMessage(error as any), 'danger');
    }
    finally {
      this.isLoading = false;
    }
  }

  private ErrorMessage(err: any): string {
    const status = err.response && err.response.status;
    if (status == 403)
      return 'Only a company admin can manage API credentials.';
    if (status == 400 && typeof err.response.data === 'string')
      return err.response.data;
    return 'please check that you have data or your internet connection is on.';
  }

  private ErrorNotification(message: string, type: string) {
    this.$bvToast.toast(message, {
      title: 'Notification',
      variant: type,
      autoHideDelay: 5000,
    });
  }
}
